/**
 * Risk Matrix Shared - Frontend
 * 
 * This module serves as a bridge between the frontend and the shared backend risk matrix code.
 * We're duplicating some functionality here temporarily until we can properly import the shared
 * code from the backend. 
 * This would need a library to avoid duplication.
 * 
 */

// Standard Risk Matrix Definitions
export const MIL882EMatrix = {
  name: "MIL-882E",
  probabilities: ["Frequent (A)", "Probable (B)", "Occasional (C)", "Remote (D)", "Improbable (E)", "Eliminated (F)"],
  severities: ["Catastrophic (1)", "Critical (2)", "Marginal (3)", "Negligible (4)"],
  matrix: [
    ["High", "High", "Serious", "Medium"],
    ["High", "High", "Serious", "Medium"],
    ["High", "Serious", "Medium", "Low"],
    ["Serious", "Medium", "Medium", "Low"],
    ["Medium", "Medium", "Medium", "Low"],
    ["Eliminated", "Eliminated", "Eliminated", "Eliminated"],
  ],
  colors: {
    High: "#FF0000",
    Serious: "#FFA500",
    Medium: "#FFFF00",
    Low: "#90EE90",
    Eliminated: "#008000",
  },
  descriptions: {
    probabilities: {
      0: "Likely to occur often in the life of an item.",
      1: "Will occur several times in the life of an item.",
      2: "Likely to occur sometime in the life of an item.",
      3: "Unlikely, but possible to occur in the life of an item",
      4: "So unlikely, it can be assumed occurrence may not be experienced in the life of an item.",
      5: "Incapable of occurence.  This level is used when potential hazards are identified and later eliminated.",
    },
    severities: {
      0: "Could result in one or more of the following:  death, permanent total disability, irreversible significant environmental impact, or monetary loss equal to or exceeding $10M.",
      1: "Could result in one or more of the following:  permanent partial disability,injuries or occupational illness that may result in hospitalization of at least three personnel, reversible significant environmental impact, or monetary loss equal to or exceeding $1M but less than $10M.",
      2: "Could result in one or more of the following:  injury or occupational illness resulting in one or more lost work day(s), reversible moderate environmental impact, or monetary loss equal to or exceeding $100K but less than $1M.",
      3: "Could result in one or more of the following:  injury or occupational illness not resulting in a lost work day, minimal environmental impact, or monetary loss less than $100K.",
    },
  },
};

export const SampleMatrix = {
  name: "Sample Matrix",
  probabilities: ["Almost certain", "Likely", "Possible", "Unlikely", "Rare"],
  severities: ["Catastrophic", "Major", "Moderate", "Minor", "Insignificant"],
  matrix: [
    ["Extreme", "Extreme", "High", "Medium", "Medium"],
    ["Extreme", "High", "High", "Medium", "Low"],
    ["High", "High", "Medium", "Low", "Low"],
    ["Medium", "Medium", "Low", "Low", "Very Low"],
    ["Medium", "Low", "Low", "Very Low", "Very Low"],
  ],
  colors: {
    Extreme: "#FF0000",
    High: "#FFA500",
    Medium: "#FFFF00",
    Low: "#90EE90",
    "Very Low": "#008000",
  },
  descriptions: {
    probabilities: {
      0: "≥90% chance of occurrence within a year",
      1: "50-89% chance of occurrence within a year",
      2: "20-49% chance of occurrence within a year",
      3: "5-19% chance of occurrence within a year",
      4: "<5% chance of occurrence within a year",
    },
    severities: {
      0: "Fatality or multiple serious injuries; >$1M financial impact",
      1: "Permanent disability or serious injury; $250K-$1M financial impact",
      2: "Lost time injury requiring medical treatment; $50K-$250K financial impact",
      3: "Minor injury requiring first aid; $10K-$50K financial impact",
      4: "No injuries or minimal first aid; <$10K financial impact",
    },
  },
};

// Standard matrices collection
export const STANDARD_MATRICES = {
  "MIL-882E": MIL882EMatrix,
  "Sample Matrix": SampleMatrix,
};

// Helper Functions
export const matrixToString = (matrix) => {
  if (typeof matrix === "string") return matrix;
  return matrix.map((row) => row.join("|")).join(";");
};

export const stringToMatrix = (matrixString) => {
  if (Array.isArray(matrixString)) return matrixString;
  return matrixString.split(";").map((row) => row.split("|"));
};

export const formatCustomMatrix = (customMatrix) => {
  if (!customMatrix) return null;

  return {
    ...customMatrix,
    matrix: typeof customMatrix.matrix === 'string' 
      ? stringToMatrix(customMatrix.matrix)
      : customMatrix.matrix,
    descriptions: customMatrix.descriptions || {
      probabilities: {},
      severities: {}
    },
    riskLevels: customMatrix.riskLevels || generateRiskLevels(customMatrix)
  };
};

export const generateRiskLevels = (matrix) => {
  const defaultWeights = {
    "#FF0000": 5, // Red
    "#FFA500": 4, // Orange
    "#FFFF00": 3, // Yellow
    "#90EE90": 2, // Light Green
    "#008000": 1  // Dark Green
  };

  const uniqueRatings = new Set();
  const matrixArray = typeof matrix.matrix === 'string'
    ? stringToMatrix(matrix.matrix)
    : matrix.matrix;

  matrixArray.forEach(row => {
    row.forEach(rating => {
      if (rating) uniqueRatings.add(rating);
    });
  });

  return Array.from(uniqueRatings).reduce((acc, rating, index) => {
    const color = matrix.colors[rating];
    const weight = defaultWeights[color] || index;
    acc[rating] = weight;
    return acc;
  }, {});
};

export const getContrastColor = (backgroundColor) => {
  // Remove any leading # from hex color
  const hex = backgroundColor.replace("#", "");

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black or white based on luminance
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

export const validateMatrix = (matrix) => {
  if (!matrix) return false;

  const requiredProps = [
    "name",
    "probabilities",
    "severities",
    "matrix",
    "colors",
  ];

  const hasRequiredProps = requiredProps.every((prop) =>
    matrix.hasOwnProperty(prop)
  );

  if (!hasRequiredProps) return false;

  // Ensure matrix dimensions match probabilities and severities
  const matrixArray = typeof matrix.matrix === 'string'
    ? stringToMatrix(matrix.matrix)
    : matrix.matrix;
  
  const rows = matrixArray.length;
  const cols = matrixArray[0]?.length || 0;

  if (
    rows !== matrix.probabilities.length ||
    cols !== matrix.severities.length ||
    !matrixArray.every((row) => row.length === cols)
  ) {
    return false;
  }

  // Check all cells have defined colors
  const uniqueRatings = new Set();
  matrixArray.forEach((row) => {
    row.forEach((cell) => {
      if (cell) uniqueRatings.add(cell);
    });
  });

  return Array.from(uniqueRatings).every((rating) => matrix.colors[rating]);
};

export const calculateRiskRating = (probability, severity, riskMatrix) => {
  // Early return with a default value if any required parameter is missing
  if (!probability || !severity || !riskMatrix) {
    return "N/A";
  }

  // Validate matrix structure
  if (!validateMatrix(riskMatrix)) {
    return "N/A";
  }

  // Ensure matrix is in array format
  const matrix = typeof riskMatrix.matrix === 'string'
    ? stringToMatrix(riskMatrix.matrix)
    : riskMatrix.matrix;

  const probIndex = riskMatrix.probabilities.indexOf(probability);
  const sevIndex = riskMatrix.severities.indexOf(severity);

  if (probIndex === -1 || sevIndex === -1) {
    console.warn(
      `Invalid probability (${probability}) or severity (${severity}) for the given risk matrix`
    );
    return "N/A";
  }

  try {
    const rating = matrix[probIndex][sevIndex];
    return rating || "N/A";
  } catch (error) {
    console.error("Error calculating risk rating:", error);
    return "N/A";
  }
};

const riskMatrixShared = {
  // Matrix definitions
  STANDARD_MATRICES,
  MIL882EMatrix,
  SampleMatrix,
  
  // Utility functions
  formatCustomMatrix,
  generateRiskLevels,
  matrixToString,
  stringToMatrix,
  getContrastColor,
  validateMatrix,
  calculateRiskRating
};

export default riskMatrixShared;